import { LineItem, Product, SearchspringSearchResult, Variant } from "@app/types";
import {
    CART_EVENTS,
    isCustomEvent,
    MODAL_EVENTS,
    POPOVER_EVENTS,
    PROMOTION_EVENTS,
    GOOGLE_ANALYTIC_EVENTS,
    CAROUSEL_EVENTS,
    COLLECTION_EVENTS,
    INTERACTION_EVENTS,
    ACCOUNT_EVENTS,
    SHOPIFY_EVENTS,
} from "@app/lib/events";
import { SearchspringRecommendItem } from "@app/hooks/use-searchspring-recommend-results";
type PageTypes = "home" | "category" | "other" | "product" | "cart" | "blog" | "article" | "account" | "page";
type CartMembershipAttributes = {
    membership_number: string;
    membership_type: string;
};

type SearchSpringVariant = {
    product_id: string;
    id: string;
    title: string;
    price: string;
    position: number;
    inventory_policy: string;
    compare_at_price?: string;
    created_at: string;
    updated_at: string;
    taxable: boolean;
    barcode: string;
    fulfillment_service: string;
    requires_shipping: boolean;
    sku: string;
    weight: string;
    weight_unit: string;
    inventory_item_id: string;
    inventory_quantity: number;
    admin_graphql_api_id: string;
    legacyResourceId: string;
    display_name: string;
    tax_code?: string;
    tracked: boolean;
    inventory_locations_count: null;
    inventory_management: "string";
    image?: string;
    image_id?: string;
    option1?: string;
    option2?: string;
    metafields: {
        id: number;
        namespace: string;
        key: string;
        value: string;
        value_type: string;
        description?: string;
        admin_graphql_api_id: string;
    }[];
    mfield_pricing_members: string;
    mfield_dotapparel_price_status: string;
};

type KeyObject = { [key: string]: string };

type CustomerAnalytics = {
    id?: string;
    loggedIn?: boolean;
    tags: string[];
    attributes?: KeyObject;
};

/**
 * Custom Pixel Events
 */
class GoogleAnalytics {
    static CART_LOCATION = "Online Store";
    static ANTAVO_ID_KEY = "antavo_id";
    static ANTAVO_TYPE_KEY = "account";
    static PAGE_NOT_FOUND_PREFIX = "/error_404";
    static DEFAULT_MEMBERSHIP_PRICE = "0.0";

    static KEYS = {
        MEMBERSHIP_PRICE: 'x-membership-price'
    }

    private analytics: any;
    private customer: CustomerAnalytics;
    private cart: { attributes: KeyObject };
    constructor() {
        const useTest = true;
        this.analytics = window.Shopify.analytics;
        this.customer = {
            id: window?.SHOPIFY_DATA?.customer?.id,
            tags: window.SHOPIFY_DATA?.customer?.tags || [],
            attributes: {},
            loggedIn: window?.SHOPIFY_DATA?.customer?.loggedIn,
        };
        this.cart = window.SHOPIFY_DATA.cart;
        this.accountEvents();
        this.handleCustomerMembership();
        this.addEventListeners();
        this.page();
    }

    /**
     * Custom event listeners
     */
    private addEventListeners() {
        /** Cart Events */
        /** Add and Remove events are both managed by this event. */
        document.addEventListener(CART_EVENTS.MODIFIED, this.cartItemEvent.bind(this));

        /** Promotion Event */
        document.addEventListener(PROMOTION_EVENTS.VIEW, this.viewPromotionEvent.bind(this));
        document.addEventListener(PROMOTION_EVENTS.SELECT, this.selectPromotionEvent.bind(this));
        /** Popover: Dismiss Event */
        document.addEventListener("popover:event", this.popoverImpressionEvents.bind(this));


        document.addEventListener(POPOVER_EVENTS.DISMISS, this.popoverEvents.bind(this));

        /** Modal Events */
        window.addEventListener("hashchange", this.modalViewEvent.bind(this));
        /** Accounts Events */
        /** Tab events  */
        document.addEventListener(ACCOUNT_EVENTS.ACCOUNT_NAVIGATION, this.accountEvent.bind(this));
        /** Interaction-events */
        /** handles the impression and selected events for upsell items */
        // triggers when a product has displayed. dispatch on product load.
        document.addEventListener(INTERACTION_EVENTS.INTERACTION_IMPRESSION, this.interactionEvents.bind(this));
        // triggers when a upsell product has been selected. dispatch on click.
        document.addEventListener(INTERACTION_EVENTS.INTERACTION_SELECT, this.interactionEvents.bind(this));
        // needs a better event name
        document.addEventListener(COLLECTION_EVENTS.COLLECTION_VIEW_LIST, this.viewItemList.bind(this));
        // Quick search
        document.addEventListener(SHOPIFY_EVENTS.QUICK_SEARCH, this.quickSearchEvent.bind(this));

        // PLP / PRODUCT CAROUSEL ITEM SELECT_ITEM EVENT;
        document.addEventListener("product:select_item", this.selectItem.bind(this));

        /** PDP Select item (variant) */
        document.addEventListener(CART_EVENTS.SELECT_VARIANT, this.selectItemPDP.bind(this));
    }

    /** Account Events
     *  Logged in user
     *
     */
    private accountEvents() {
        /** Logged out customer events  */
        if (!this.customer.loggedIn) return;

        /** logged in customer events  */
        /** pageLoadHashedEmail event */
        const event = GOOGLE_ANALYTIC_EVENTS.PAGE_LOAD_HASHED_EMAIL;
        const payload = {
            event,
            pageloadhashedemail: window.SHOPIFY_DATA.customer.shaEmail,
        };
        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /**
     * Events Covered: 'home' | 'category' | 'other' | 'product' | 'cart'
     *
     * purchase event is handled in pixel events under the 'purchase' event.
     */
    private dynamicRemarketingEvents(type: PageTypes) {
        const event = GOOGLE_ANALYTIC_EVENTS.DYNAMIC_REMARKETING;
        const ecommPagetype = type;
        switch (type) {
            case "home": {
                //index page
                const payload = {
                    event,
                    ecommPagetype,
                };
                this.pushToWebPixel(`custom_${event}`, payload);
                return;
            }
            case "cart": {
                // cart
                const cart = window?.SHOPIFY_DATA?.cart;
                const ecomProID = cart.items.reduce((acc: string[], lineItem) => {
                    if (!lineItem?.sku) return acc;
                    const { id } = this.getSkuInfo(lineItem?.sku)
                    if (!id) return acc;
                    acc.push(id);
                    return acc
                }, [])
                const ecommTotalValue = cart.total_price / 100;

                const payload = {
                    event,
                    ecommPagetype,
                    ecomProID,
                    ecommTotalValue,
                };

                this.pushToWebPixel(`custom_${event}`, payload);

                return;
            }
            case "product": {
                const product = window?.SHOPIFY_DATA?.product;
                const ecomProID = product.variants.reduce((acc: string[], lineItem) => {
                    if (!lineItem?.sku) return acc;
                    const { id } = this.getSkuInfo(lineItem?.sku)
                    if (!id) return acc;
                    acc.push(id);
                    return acc
                }, [])

                const ecommTotalValue = product.variants.reduce((total, variant) => (total += variant.price), 0) / 100;
                const payload = {
                    event,
                    ecommPagetype,
                    ecomProID,
                    ecommTotalValue,
                };
                this.pushToWebPixel(`custom_${event}`, payload);
                return;
            }
            case "category": {
                const payload = {
                    event,
                    ecommPagetype,
                };
                this.pushToWebPixel(`custom_${event}`, payload);
                return;
            }
            case "other": {
                const payload = {
                    event,
                    ecommPagetype,
                };
                this.pushToWebPixel(`custom_${event}`, payload);
                return;
            }
        }
    }

    private handlePageLevel(url: URL): { [key: string]: string } {
        const path = url.pathname.split("/");
        const key = "pageLevel";
        return path.reduce((levels: { [key: string]: string }, level: string, index: number) => {
            const indexPlus = index + 1;
            if (index === 0) {
                levels[`${key}${indexPlus}`] = "Home";
                return levels;
            }
            if (!level.length || !level) return levels;
            const deHandleLevel = level.split("-").join(" ");
            const levelFirstLetter = deHandleLevel[0].toUpperCase();
            const rest = deHandleLevel.slice(1, deHandleLevel.length);
            levels[`${key}${indexPlus}`] = levelFirstLetter.concat(rest);
            return levels;
        }, {});
    }

    /**
     * Page view event
     * Creates and sends the page view event data to Shopify Web Pixel.
     */
    private pageViewEvent(config: {
        pageType: PageTypes;
        pageTitle?: string;
        hasDynamicRemarketingEvents?: boolean;
        handlePrefix?: string;
    }) {
        const path = config?.handlePrefix
            ? config?.handlePrefix.concat(window.location.pathname)
            : window.location.pathname;
        const pageTitle = config?.pageTitle ?? document.title;
        const pageType = config.pageType;
        const event = GOOGLE_ANALYTIC_EVENTS.PAGE_VIEW;
        const data = {
            event,
            pageTitle,
            pagePath: path,
            templateType: pageType,
            ...this.handlePageLevel(new URL(window.location.href)),
            ...this.getCustomerDetail(),
            userSelectedStore: "none",
            loyaltyLevel: "",
            loyaltyPoints: "",
        };

        if (config.hasDynamicRemarketingEvents && config.pageType) {
            const key = config.pageType === "page" ? "other" : config.pageType;
            this.dynamicRemarketingEvents(key);
        }

        this.pushToWebPixel(`custom_${event}`, data);
    }

    private page() {
        const path = window.location.pathname;
        const currency = window.SHOPIFY_DATA.shop.currency;
        const payload = { data: {} as any };

        if (path === "/") {
            this.pageViewEvent({ pageType: "home", hasDynamicRemarketingEvents: true });
        } else if (path.includes("/cart")) {
            const items = window.SHOPIFY_DATA.cart.items.map((item, index) => ({
                ...this.createItemLine({
                    index: index,
                    productTitle: item.product_title,
                    currency,
                    category: {
                        fullUrl: true,
                    },
                    item: {
                        quantity: item.quantity.toString(),
                        sku: item.sku,
                        title: item.title,
                        vendor: item.vendor,
                        url: window.location.pathname,
                        price: item.price,
                        original_price: item.line_price,
                        member_price: item.properties[GoogleAnalytics.KEYS.MEMBERSHIP_PRICE],
                    },
                }),
            }));

            const cartTotal = this.parseShopifyMoney(window.SHOPIFY_DATA.cart.items_subtotal_price);
            payload.data = {
                event: GOOGLE_ANALYTIC_EVENTS.VIEW_CART,
                ecommerce: {
                    currency: currency,
                    value: cartTotal,
                    coupon: "",
                    items,
                },
            };

            this.pageViewEvent({ pageType: "cart", hasDynamicRemarketingEvents: true });
        } else if (path.includes("/products/")) {
            const product = window?.SHOPIFY_DATA?.product;
            const defaultVariant = window.SHOPIFY_DATA.variants.default;

            const item = this.createItemLine({
                index: 0,
                productTitle: product.title,
                currency,
                category: {
                    fullUrl: true,
                },
                item: {
                    quantity: "1",
                    sku: defaultVariant.sku,
                    title: product.title,
                    vendor: product.vendor,
                    url: window.location.pathname,
                    price: (defaultVariant.price),
                    original_price: defaultVariant.compared_at_price,
                    member_price: defaultVariant.member_price,
                },
            });
            payload.data = {
                event: GOOGLE_ANALYTIC_EVENTS.VIEW_ITEM,
                category: "Ecommerce",
                ecommerce: {
                    currency,
                    coupon: "",
                    value: this.parseShopifyMoney(defaultVariant.price),
                    items: [item],
                },
            };
            this.pageViewEvent({ pageType: "product", hasDynamicRemarketingEvents: true });
        } else if (path.includes("/search") && window.SHOPIFY_DATA.searchQuery) {
            payload.data = {
                event: GOOGLE_ANALYTIC_EVENTS.VIEW_SEARCH_RESULTS,
                action: GOOGLE_ANALYTIC_EVENTS.VIEW_SEARCH_RESULTS,
                search_term: window.SHOPIFY_DATA.searchQuery,
            };

            this.pageViewEvent({ pageType: "other" });
        } else if (path.includes("/404") || window.SHOPIFY_DATA.pageNotFound) {
            payload.data = {
                event: GOOGLE_ANALYTIC_EVENTS["404_ERROR"],
                action: "404 Response",
                category: "Error",
                label: `${window.location.href}`,
            };
            this.pageViewEvent({ pageType: "other", handlePrefix: GoogleAnalytics.PAGE_NOT_FOUND_PREFIX });
        } else if (path.includes("/pages") && window.SHOPIFY_DATA.page?.id) {
            const page = window.SHOPIFY_DATA.page?.handle ?? null;
            if (!page) return;
            this.pageViewEvent({ pageType: "page", hasDynamicRemarketingEvents: true });
        } else if (path.includes("/collections")) {
            const collection = window.SHOPIFY_DATA.collection?.handle ?? null;
            if (!collection) return;
            this.pageViewEvent({ pageType: "category", hasDynamicRemarketingEvents: true });
        } else if (path.includes("/blogs") && window.SHOPIFY_DATA.blog.article?.handle) {
            this.pageViewEvent({ pageType: "article" });
        } else if (path.includes("/blogs") && window.SHOPIFY_DATA.blog?.handle) {
            this.pageViewEvent({ pageType: "blog" });
        } else if (path.includes("/account")) {
            this.pageViewEvent({ pageType: "account" });
        } else {
            /** Any other page that is not mentioned above. */
            this.pageViewEvent({ pageType: "other", hasDynamicRemarketingEvents: true });
        }
        if (!payload.data?.event) return;
        const event = `custom_${payload.data?.event}`;
        this.pushToWebPixel(event, payload.data);
    }

    private quickSearchEvent(e: Event) {
        if (!isCustomEvent(e)) return;
        const searchQuery = e.detail.searchQuery;

        const data = {
            event: GOOGLE_ANALYTIC_EVENTS.VIEW_SEARCH_RESULTS,
            action: GOOGLE_ANALYTIC_EVENTS.VIEW_SEARCH_RESULTS,
            search_term: searchQuery,
        };
        const event = `custom_${data?.event}`;
        this.pushToWebPixel(event, data);
    }

    private interactionEvents(e: Event) {
        if (!isCustomEvent(e)) return;
        const data = e.detail;
        const payload = {
            event: data.event,
            eventAction: data.eventAction,
            eventModuleName: data.eventModuleName,
            eventActionText: data.eventActionText,
            eventActionIdentifier: data.eventActionIdentifier,
        };
        this.pushToWebPixel(`custom_${data.event}`, payload);
    }

    /**
     * Handles account navigation events
     * Types - 'tabs'
     *
     * Tabs - sends data to GA whenever a tab value has been selected in
     * the accounts menu.
     *
     * @param e
     * @returns
     */
    private accountEvent(e: Event) {
        if (!isCustomEvent(e)) return;
        const location = window.location.href;
        const event = GOOGLE_ANALYTIC_EVENTS.CALL_TO_ACTION;
        const eventType = "click";

        const data = e.detail as { type: "tab"; label: string };

        const payload = {
            event,
            eventType,
            location,
            clickText: data.label,
        };
        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /**
     * Modal events
     *
     * Not sure if this is actually required.
     * @param e
     */
    private modalViewEvent(e: Event) {
        const modalKey = window.location.hash;
        const actionIdentifier = !this.customer.loggedIn ? "Unknown user" : "Known member";
        switch (modalKey) {
            case "#account": {
                const event = GOOGLE_ANALYTIC_EVENTS.JOIN_MODAL;
                const payload = {
                    event,
                    action: event,
                    moduleName: "Join in store modal",
                    actionIdentifier,
                    clickText: "Join in store",
                };
                this.pushToWebPixel(`custom_${event}`, payload);
                return;
            }
            case "#account?tab=account-login": {
                const event = GOOGLE_ANALYTIC_EVENTS.SIGN_IN_MODAL;
                const payload = {
                    action: event,
                    moduleName: "Your Account modal",
                    actionIdentifier,
                    clickText: "Already a member? Sign in",
                };
                this.pushToWebPixel(`custom_${event}`, payload);
            }
        }
    }

    private popoverImpressionEvents(e: Event) {
        if (!isCustomEvent(e)) return;
        const element = e.detail.ele as HTMLElement;
        if (!element) return;
        const popupInfo = element.dataset?.info;
        const action = e.detail.action
        if (!popupInfo) return;
        const jsonPopupInfo = JSON.parse(popupInfo);
        const text = element.innerText;
        const event = GOOGLE_ANALYTIC_EVENTS.SIGN_IN_PROMPT;
        const payload = {
            event,
            action: action,
            moduleName: jsonPopupInfo?.module_name,
            actionIdentifier: `${jsonPopupInfo?.action}`,
            clickText: text,
        };
        this.pushToWebPixel(`custom_${event}`, payload);
    }

    private popoverEvents(e: Event) {
        if (!isCustomEvent(e)) return;
        const popupInfo = e.detail.ele.dataset?.info;
        if (!popupInfo) return;
        const jsonPopupInfo = JSON.parse(popupInfo);
        const event = GOOGLE_ANALYTIC_EVENTS.JOIN_MODAL;
        const payload = {
            action: event,
            moduleName: jsonPopupInfo?.title,
            actionIdentifier: `${jsonPopupInfo?.action} | dismissed`,
            clickText: jsonPopupInfo?.title,
        };
        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /**
     * When the cart has been modified (item added, updated or deleted)
     * This even will trigger
     * @param e
     */
    private cartItemEvent(e: Event) {
        if (!isCustomEvent(e)) return;
        try {

            const lineItemEvent = e.detail?.item;
            const lineItem = lineItemEvent?.item as LineItem;
            if (!lineItem) return;
            const action = lineItemEvent.action;
            const event = action === "added" ? GOOGLE_ANALYTIC_EVENTS.ADD_TO_CART : GOOGLE_ANALYTIC_EVENTS.REMOVE_FROM_CART;
            const currency = window.SHOPIFY_DATA.shop.currency;

            const line = this.createItemLine({
                currency,
                productTitle: lineItem.product_title,
                category: { fullUrl: true },
                item: {
                    quantity: "1",
                    sku: lineItem.sku ?? "",
                    title: lineItem.title,
                    vendor: lineItem.vendor,
                    url: lineItem.url,
                    price: lineItem?.price,
                    original_price: lineItem.original_price,
                    member_price: lineItem.properties[GoogleAnalytics.KEYS.MEMBERSHIP_PRICE]
                },
                index: 0,
            });

            const payload = {
                event,
                category: "Ecommerce",
                action: event,
                ecommerce: {
                    currency,
                    value: this.parseShopifyMoney(lineItem.price),
                    items: [line],
                },
            };

            this.pushToWebPixel(`custom_${event}`, payload);
        } catch (err) { }
    }

    /**
     * Triggered by a customer viewing a collection page PLP.
     *
     * Customer Event: list:select_product
     * GA Event: view_item_list
     * @param e
     */
    private viewItemList(e: Event) {
        if (!isCustomEvent(e)) return;
        const event = GOOGLE_ANALYTIC_EVENTS.VIEW_ITEM_LIST;
        const currency = window.SHOPIFY_DATA.shop.currency;
        const collection = window.SHOPIFY_DATA.collection;
        const collectionItems = e.detail as SearchspringSearchResult[];

        const collectionValue = collectionItems
            .reduce((total, item) => {
                if (!item.price) return total;
                const lineTotal = parseFloat(parseFloat(item.price).toFixed(2));
                return (total += lineTotal);
            }, 0)
            .toFixed(2);

        const items = collectionItems.map((item, index) => {
            const variantList = this.parseSearchSpringVariants(item?.variants);
            const memberPrice = this.getMemberPrice(variantList);
            return this.createItemLine({
                index,
                productTitle: item.name,
                category: { fullUrl: true },
                currency,
                item: {
                    quantity: "1",
                    sku: item.sku,
                    title: item.name,
                    vendor: item.brand,
                    url: item.url,
                    price: this.fixedDecimalFromString(item.price),
                    original_price: this.fixedDecimalFromString(item.price),
                    member_price: memberPrice,
                    item_list_name: collection.title,
                    item_list_id: collection.handle,
                },
            }, false);
        });

        const payload = {
            event,
            ecommerce: {
                currency,
                coupon: "",
                itemListName: collection.title,
                itemListId: collection.id,
                value: this.fixedDecimalFromString(collectionValue),
                items,
            },
        };

        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /**
     *
     * @param e
     * @returns
     */
    private viewPromotionEvent(e: Event) {
        if (!isCustomEvent(e)) return;
        const event = GOOGLE_ANALYTIC_EVENTS.VIEW_PROMOTION;
        const items = e.detail;
        const coupon = "";
        const payload = {
            event,
            ecommerce: {
                coupon,
                ...items,
            },
        };

        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /**
     *
     * @param e
     * @returns
     */
    private selectPromotionEvent(e: Event) {
        if (!isCustomEvent(e)) return;
        const event = GOOGLE_ANALYTIC_EVENTS.SELECT_PROMOTION;
        const items = e.detail;
        const coupon = "";
        const payload = {
            event,
            ecommerce: {
                coupon,
                ...items,
            },
        };

        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /** SELECT ITEM EVENTS */

    /** Used on PLP or product carousels */
    private selectItem(e: Event) {
        type selectEvent = {
            source: "PLP" | "Carousel";
            variantId: number;
            index: number;
            shopifyVariantData?: Variant;
            searchSearchVariantData?: SearchspringSearchResult;
        };
        if (!isCustomEvent(e)) return;
        const item = e.detail as selectEvent;
        const currency = window.SHOPIFY_DATA.shop.currency;
        const event = GOOGLE_ANALYTIC_EVENTS.SELECT_ITEM;
        const collection = window.SHOPIFY_DATA.collection

        switch (item.source) {
            case "PLP": {
                const variant = item.searchSearchVariantData?.parsedVariants?.shift() ?? null;
                if (!variant) return;
                const parsedVariant = variant as SearchSpringVariant;
                const memberPrice = parsedVariant.metafields.find((meta) => meta.key === "members");
                const parseMemberPrice = this.fixedDecimalFromString(memberPrice?.value)
                const line = this.createItemLine({
                    index: item.index,
                    productTitle: item.searchSearchVariantData?.name ?? "",
                    currency: window.SHOPIFY_DATA.shop.currency,
                    category: { fullUrl: true },
                    item: {
                        quantity: "1",
                        sku: parsedVariant.sku,
                        title: parsedVariant.title,
                        vendor: item.searchSearchVariantData?.brand ?? "",
                        url: item.searchSearchVariantData?.url ?? "",
                        price: this.fixedDecimalFromString(parsedVariant.price),
                        original_price: this.fixedDecimalFromString(parsedVariant.compare_at_price ?? parsedVariant.price),
                        member_price: parseMemberPrice <= 0 ? "none" : parseMemberPrice,
                        total_discount: "",
                        item_list_id: collection?.handle ?? "",
                        item_list_name: collection?.title ?? "",
                    },
                }, false);

                const payload = {
                    event: event,
                    ecommerce: {
                        currency,
                        item_list_id: collection?.handle ?? "",
                        item_list_name: collection?.title ?? "",
                        coupon: "",
                        value: this.fixedDecimalFromString(parsedVariant.price),
                        items: [line],
                    },
                };
                this.pushToWebPixel(`custom_${event}`, payload);

                break;
            }
            case "Carousel": {
                const product = item.searchSearchVariantData as SearchspringRecommendItem<any>;
                const line = this.createItemLine({
                    index: item.index,
                    productTitle: product.name ?? "",
                    currency: window.SHOPIFY_DATA.shop.currency,
                    category: { fullUrl: true },
                    item: {
                        quantity: "1",
                        sku: product.sku,
                        title: product.name,
                        vendor: item.searchSearchVariantData?.brand ?? "",
                        url: item.searchSearchVariantData?.url ?? "",
                        price: this.fixedDecimalFromString(product.price),
                        original_price: this.fixedDecimalFromString(product.price),
                        member_price: this.fixedDecimalFromString(product.msrp),
                        total_discount: "",

                    },
                }, false);

                const payload = {
                    event: event,
                    ecommerce: {
                        currency,
                        item_list_id: "",
                        item_list_name: "",
                        coupon: "",
                        value: this.fixedDecimalFromString(product.price),
                        items: [line],
                    },
                };
                this.pushToWebPixel(`custom_${event}`, payload);

                break;
            }
            default: {
            }
        }
    }

    /**
     * Event is triggered once a variant item has been selected on the PDP
     * @param e
     * @returns
     */
    private selectItemPDP(e: Event) {
        if (!isCustomEvent(e)) return;
        const id = e.detail;
        const product = window?.SHOPIFY_DATA?.product;
        const variantIndex = product?.variants.findIndex((variant) => variant.id == id);
        const variant = product?.variants[variantIndex];
        if (!variant) return;
        const event = GOOGLE_ANALYTIC_EVENTS.SELECT_ITEM;
        const memberPrice = window?.SHOPIFY_DATA?.variants.default.member_price;
        const currency = window.SHOPIFY_DATA.shop.currency;

        const line = this.createItemLine({
            currency: window.SHOPIFY_DATA.shop.currency,
            index: variantIndex,
            category: { fullUrl: true },
            productTitle: product.title,
            item: {
                quantity: "1",
                title: variant.title,
                sku: variant.sku ?? "",
                price: variant.price,
                vendor: product.vendor,
                url: `/products/${product.handle}`,
                member_price: memberPrice,
                original_price: variant.compare_at_price,
            },
        });

        const payload = {
            event: event,
            ecommerce: {
                currency,
                item_list_name: "",
                item_list_id: "",
                coupon: "",
                value: this.parseShopifyMoney(product.price),
                items: [line],
            },
        };
        this.pushToWebPixel(`custom_${event}`, payload);
    }

    /** END SELECT ITEM EVENTS */

    /** Util Functions  */

    private createItemLine(config: {
        index: number;
        productTitle: string;
        currency: string;
        parseMoney?: boolean,
        category: {
            fullUrl: boolean;
            concat?: string;
        };
        item: {
            quantity: string;
            sku: string;
            title: string;
            vendor: string;
            url: string;
            price: string | number;
            original_price: string | number;
            member_price?: string | number;
            line_price?: string | number;
            properties?: Record<string, any>;
            total_discount?: string | number;
            item_list_id?: string;
            item_list_name?: string;
        };
        additionalItems?: { [key: string]: any };
    }, parseMoney: boolean = true) {
        const { index, currency, item, category } = config;

        const priceDetails = (parseMoney: boolean) => {
            if (parseMoney) {
                return {
                    price: this.parseShopifyMoney(item.price),
                    regular_price: this.parseShopifyMoney(item?.original_price ?? item.price),
                    member_price: this.handleMemberPrice(item.member_price),
                }
            }
            return {
                price: item.price,
                regular_price: item?.original_price ?? item.price,
                member_price: item.member_price
            }

        }


        return {
            index,
            currency,
            quantity: parseInt(item.quantity),
            item_id: item.sku,
            item_name: item.title,
            item_brand: item.vendor,
            item_variant: this.getItemSku(item.sku),
            cart_location: GoogleAnalytics.CART_LOCATION,
            discount: item.total_discount ?? "none",
            item_list_id: item.item_list_id ?? "",
            item_list_name: item.item_list_name ?? "",
            affiliation: "",
            coupon: "",
            ...(config.additionalItems ? config.additionalItems : {}),
            ...this.itemCategory(new URL(window.location.origin.concat(item.url)), item.sku),
            ...this.getCustomerDetail(),
            ...priceDetails(parseMoney)
        };
    }

    private pushToWebPixel(event: string, payload: { [key: string]: any }) {
        this.analytics.publish(event, payload);
    }

    private parseShopifyMoney(inputCents: number | string | null, includeCurrencyCode: boolean = false) {
        if (!inputCents) return 0.0;
        const cents = typeof inputCents === "string" ? parseInt(inputCents) : inputCents;
        const currency = window.SHOPIFY_DATA.shop.currency;
        const dollars = (cents / 100).toLocaleString("en-US", { style: "currency", currency });
        if (!includeCurrencyCode) {
            const dollarAmountString = dollars.split("$").pop() ?? '0.0'
            return this.fixedDecimalFromString(dollarAmountString)
        }
        return dollars;
    }

    private getItemSku(itemCode: string) {
        return itemCode?.split("/").shift() ?? itemCode;
    }

    private getSkuInfo(sku: string) {
        if (typeof sku !== "string") return { id: "", info: "" };
        const splitIndex = "/";
        const index = sku.indexOf(splitIndex);
        const id = sku.slice(0, index);
        const info = sku.slice(index + 1);
        return { id, info };
    };


    private getLineItemDiscount(lineItem: LineItem) {
        //todo
    }

    private itemCategory(url: URL, sku?: string) {
        type Category = {
            index: number;
            handle: string;
            title: string;
        };

        const itemCategories = url.pathname
            .split("/")
            .filter((line) => line)
            .reduce((categories: Category[], handle, index) => {
                const title = handle
                    .split("-")
                    .reduce((acc: string, word: string) => {
                        if (!word?.length) return acc;
                        const firstLetter = word[0].toUpperCase();
                        return acc.concat(firstLetter.concat(word.slice(1, word.length))).concat(" ");
                    }, "")
                    .trim();
                const category = {
                    index: index + 2,
                    handle,
                    title,
                };
                categories.push(category);
                return categories;
            }, []);

        const allTitles = itemCategories.map((_item: { title: string }) => _item.title).join("/");
        const first_entry = [
            {
                index: 0,
                handle: "",
                title: allTitles,
            },
        ];

        const lineCategories = first_entry.concat(itemCategories);

        if (!!sku) {
            const firstSeparatorIndex = sku.indexOf("/");
            const sliceIndex = firstSeparatorIndex + 1;
            const lastCategory = sku.slice(sliceIndex);
            lineCategories.push({
                index: lineCategories.length + 1,
                handle: "",
                title: lastCategory,
            });
        }

        const totalItemCategories = lineCategories.reduce(
            (items: { [key: string]: string }, item: { index: number; handle: string; title: string }) => {
                const key = "item_category".concat(item.index > 1 ? item.index.toString() : "");
                items[key] = item.title;
                return items;
            },
            {},
        );

        return totalItemCategories;
    }

    private parseSearchSpringVariants(variantsString: string): SearchSpringVariant[] {
        if (!variantsString) return [];
        const variantsJSON = JSON.parse(((variantsString as string) ?? [])?.split("&quot;")?.join('"'));
        return variantsJSON;
    }

    private fixedDecimalFromString(value?: string | number): number {
        if (!value) return 0.0
        if (typeof value === "number") return this.fixedDecimalFromString(value.toString())
        const float = parseFloat(value);
        if (isNaN(float)) return 0.0
        return parseFloat(float.toFixed(2))
    }

    private getMemberPrice(variants: SearchSpringVariant[]) {
        if (!variants.length) return "none";
        const variant = variants.pop();
        if (!variant) return "none";
        return this.fixedDecimalFromString(variant.mfield_pricing_members)
    }

    private handleMemberPrice(value?: string | number) {
        if (!value) return 'none';
        const parseToFixedFloat = this.fixedDecimalFromString(value)
        if (parseToFixedFloat <= 0) return 'none'
        return parseToFixedFloat
    }

    /**
     * On Page load checks if customer is logged in and customer tags
     * and applies antavo loyalty information to the customer's cart attributes.
     *
     * Also applies the object to the class Customer object.
     *
     * @returns
     */
    private handleCustomerMembership() {
        const defaultAttributes = {
            membership_number: "none",
            membership_type: "guest",
        };
        // Check if customer is logged in.
        if (!this.customer.loggedIn) {
            // apply guest membership to details to cart attribute
            this.applyCartAttributes(defaultAttributes);
            this.customer.attributes = defaultAttributes;
            return;
        }

        // Pull membership details from customer
        const customerMembership = this.customer.tags.reduce(
            (acc: CartMembershipAttributes, tag: string) => {
                const [prefix, value] = tag.split(":");
                if (prefix.includes(GoogleAnalytics.ANTAVO_ID_KEY)) {
                    acc["membership_number"] = value;
                }
                if (prefix.includes(GoogleAnalytics.ANTAVO_TYPE_KEY)) {
                    acc["membership_type"] = 'member';
                }
                return acc;
            },
            {
                membership_number: "",
                membership_type: "",
            },
        );

        if (!customerMembership?.membership_number || !customerMembership?.membership_type) {
            // apply guest membership to details to cart attribute
            this.applyCartAttributes(defaultAttributes);
            this.customer.attributes = defaultAttributes;
            return;
        }

        const membershipDetails = this.cart.attributes as CartMembershipAttributes;
        // Check if attributes exist on cart.
        if (!membershipDetails?.membership_number || !membershipDetails?.membership_type) {
            // apply membership attributes
            this.applyCartAttributes(customerMembership);
            this.customer.attributes = customerMembership;
            return;
        }

        // Check if cart attribute match with customer attribute
        if (
            membershipDetails?.membership_number !== customerMembership?.membership_number ||
            membershipDetails?.membership_type !== customerMembership?.membership_type
        ) {
            this.applyCartAttributes(customerMembership);
            this.customer.attributes = customerMembership;
            return
        }

        this.customer.attributes = customerMembership;
    }

    private applyCartAttributes(attributes: CartMembershipAttributes) {
        fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                attributes: attributes,
            }),
        });
    }

    /**
     * Customer antavo loyalty member attributes
     * @returns {CartMembershipAttributes}
     */
    private getCustomerDetail(): CartMembershipAttributes {
        return {
            membership_number: this.customer.attributes?.membership_number ?? "none",
            membership_type: this.customer.attributes?.membership_type ?? "guest",
        };
    }
}

export const GA = new GoogleAnalytics();
